/* eslint-disable camelcase */
import { useCallback, useEffect, useRef } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { v4 as uuidv4 } from 'uuid';
import { useUser } from '@app/contexts/userContext';
import { useChatStore } from '@app/contexts/chatStoreContext';
import { idsMatch } from '@app/helpers/normalizeId';
import { chatSocketService } from '@app/services/chat-socket.service';
import { GET_GROUP_MESSAGES } from '../../apollo/queries';
import {
  SEND_GROUP_MESSAGE,
} from '../../apollo/mutations';

export function useGroupChats() {
  const { user: authUser } = useUser();
  const {
    chatState,
    updateState,
    addOptimisticUpdate,
    removeOptimisticUpdate,
  } = useChatStore();

  const loadingGroupMessagesRef = useRef(new Set());
  const groupChatsFetchRef = useRef({ isFetching: false, lastFetchAt: 0, retryTimeout: null });

  // Apollo as API client only -- no caching, results handled inline
  const [getGroupChatMessages] = useLazyQuery(GET_GROUP_MESSAGES, {
    fetchPolicy: 'no-cache',
  });

  const [sendGroupMessageMutation] = useMutation(SEND_GROUP_MESSAGE, {
    fetchPolicy: 'no-cache',
  });

  const loadGroupChats = useCallback(async () => {
    if (!authUser) return;

    const now = Date.now();
    const fetchState = groupChatsFetchRef.current;

    if (fetchState.isFetching || now - fetchState.lastFetchAt < 5000) {
      return;
    }

    try {
      fetchState.isFetching = true;
      fetchState.lastFetchAt = now;
      updateState({ isLoading: true, error: null });

      // TODO: Add getMyGroupChats query when available
    } catch (error) {
      updateState({ error: error.message });

      if (fetchState.retryTimeout) {
        clearTimeout(fetchState.retryTimeout);
      }

      fetchState.retryTimeout = setTimeout(() => {
        loadGroupChats();
      }, 5000);
    } finally {
      fetchState.isFetching = false;
      updateState({ isLoading: false });
    }
  }, [authUser, updateState]);

  const loadGroupChatMessages = useCallback(
    async (groupChatId, cursor = null) => {
      if (!groupChatId) {
        return { messages: [], pageInfo: null, success: false };
      }

      if (loadingGroupMessagesRef.current.has(groupChatId)) {
        return { messages: [], pageInfo: null, success: false };
      }

      try {
        loadingGroupMessagesRef.current.add(groupChatId);

        const result = await getGroupChatMessages({
          variables: {
            groupChatId,
            first: 20,
            after: cursor,
          },
        });

        const response = result?.data?.groupMessages;

        if (response?.edges) {
          const edges = response.edges || [];
          const pageInfo = response.pageInfo || {
            hasNextPage: false,
            hasPreviousPage: false,
            startCursor: null,
            endCursor: null,
          };

          const messages = edges.map((edge) => edge.node);

          return { messages, pageInfo, success: true };
        }

        if (Array.isArray(response)) {
          const pageInfo = {
            hasNextPage: false,
            hasPreviousPage: false,
            startCursor: null,
            endCursor: null,
          };

          return {
            messages: response,
            pageInfo,
            success: true,
          };
        }

        return {
          messages: [],
          pageInfo: null,
          success: false,
          error: 'Unexpected response format',
        };
      } catch (error) {
        return {
          messages: [],
          pageInfo: null,
          success: false,
          error: error.message,
        };
      } finally {
        loadingGroupMessagesRef.current.delete(groupChatId);
      }
    },
    [getGroupChatMessages]
  );

  const loadMoreGroupMessages = useCallback(
    async (groupChatId, endCursor) => {
      if (!groupChatId || !endCursor) {
        return { messages: [], pageInfo: null, success: false };
      }

      try {
        const result = await getGroupChatMessages({
          variables: {
            groupChatId,
            first: 20,
            after: endCursor,
          },
        });

        const response = result?.data?.groupMessages;

        if (response?.edges) {
          const edges = response.edges || [];
          const pageInfo = response.pageInfo || {
            hasNextPage: false,
            hasPreviousPage: false,
            startCursor: null,
            endCursor: null,
          };

          const messages = edges.map((edge) => edge.node);

          return { messages, pageInfo, success: true };
        }

        if (Array.isArray(response)) {
          return {
            messages: [],
            pageInfo: {
              hasNextPage: false,
              hasPreviousPage: false,
              startCursor: null,
              endCursor: null,
            },
            success: true,
          };
        }

        return {
          messages: [],
          pageInfo: null,
          success: false,
          error: 'Unexpected response format',
        };
      } catch (error) {
        return {
          messages: [],
          pageInfo: null,
          success: false,
          error: error.message,
        };
      }
    },
    [getGroupChatMessages]
  );

  const markGroupChatAsRead = useCallback(
    async (groupChatId) => {
      if (!groupChatId) return;

      updateState((prev) => ({
        ...prev,
        groupChatUnreadCounts: {
          ...prev.groupChatUnreadCounts,
          [groupChatId]: 0,
        },
      }));
    },
    [updateState]
  );

  const joinGroupChat = useCallback(
    async (groupChat) => {
      if (!groupChat || !authUser) return false;

      if (idsMatch(chatState.activeGroupChat?.id, groupChat.id)) {
        return true;
      }

      updateState({
        activeGroupChat: groupChat,
        activeConversation: null,
        activeChatUser: null,
        groupChatMessages: [],
      });

      // Load messages and update state directly
      const result = await loadGroupChatMessages(groupChat.id);
      if (result.success && result.messages.length > 0) {
        updateState({ groupChatMessages: result.messages });
      }

      const success = await chatSocketService.joinGroupChat(groupChat.id, authUser.id);

      if (success) {
        markGroupChatAsRead(groupChat.id);
      }

      return success;
    },
    [
      authUser,
      chatState.activeGroupChat,
      loadGroupChatMessages,
      updateState,
      markGroupChatAsRead,
    ]
  );

  // Leave group chat
  const leaveGroupChat = useCallback(() => {
    chatSocketService.leaveGroupChat();
    updateState({
      activeGroupChat: null,
      groupChatMessages: [],
      groupChatMembers: [],
    });
  }, [updateState]);

  const sendGroupMessage = useCallback(
    async ({ groupChatId, message, type = 'TEXT', metadata = null, media_urls = [] }) => {
      const chatId = groupChatId || chatState.activeGroupChat?.id;
      if (!chatId || typeof message !== 'string' || (!message?.trim() && media_urls.length < 1))
        return false;

      try {
        // Normalize metadata: must be a plain object or null for GraphQL JSON scalar.
        // An empty array [] or empty object {} would be rejected by the backend.
        const rawMetadata = {
          ...(metadata && typeof metadata === 'object' && !Array.isArray(metadata) ? metadata : {}),
          ...(media_urls && media_urls.length > 0 ? { media_urls } : {}),
        };
        const messageMetadata = Object.keys(rawMetadata).length > 0 ? rawMetadata : null;

        const messageUuid = uuidv4();

        // Single message shape matching GraphQL SendGroupMessageInput.
        // chat-socket.service.js reads these same fields and derives
        // eventId from groupChatId, creates content from message, etc.
        const messageData = {
          uuid: messageUuid,
          groupChatId: chatId,
          message: message.trim(),
          type,
          metadata: messageMetadata,
          media_urls,
          senderId: authUser.id,
        };

        const tempMessage = {
          ...messageData,
          id: messageUuid,
          createdAt: new Date().toISOString(),
          sender: {
            id: authUser.id,
            name: authUser.name || authUser.first_name || 'You',
            avatar: authUser.avatar || null,
          },
        };

        addOptimisticUpdate(messageUuid);

        updateState((prev) => ({
          ...prev,
          groupChatMessages: [...prev.groupChatMessages, tempMessage],
        }));

        if (process.env.NODE_ENV === 'development') {
          console.log('[Chat] Sending group message, uuid:', messageUuid, 'group:', chatId);
        }

        // Dual send: socket for real-time delivery, GraphQL for DB persistence.
        chatSocketService.sendGroupMessage({
          ...messageData,
          sender: authUser,
        });

        try {
          const result = await sendGroupMessageMutation({
            variables: { input: messageData },
          });
          const savedMessage = result?.data?.sendGroupMessage;

          if (savedMessage) {
            // Replace optimistic message with the real DB message
            removeOptimisticUpdate(messageUuid);
            updateState((prev) => ({
              ...prev,
              groupChatMessages: prev.groupChatMessages.map((msg) =>
                msg.uuid === messageUuid
                  ? { ...savedMessage, uuid: messageUuid, sender: tempMessage.sender }
                  : msg
              ),
            }));

            if (process.env.NODE_ENV === 'development') {
              console.log('[Chat] Group message persisted, db id:', savedMessage.id);
            }
          }

          return savedMessage || tempMessage;
        } catch (mutationError) {
          console.error('[Chat] Failed to persist group message:', mutationError.message);
          removeOptimisticUpdate(messageUuid);
          updateState((prev) => ({
            ...prev,
            groupChatMessages: prev.groupChatMessages.filter(
              (msg) => msg.uuid !== messageUuid
            ),
            error: 'Failed to save message',
          }));
          return false;
        }
      } catch (error) {
        updateState({ error: error.message });
        return false;
      }
    },
    [
      chatState.activeGroupChat,
      chatState.groupChatMessages,
      authUser,
      sendGroupMessageMutation,
      updateState,
      addOptimisticUpdate,
      removeOptimisticUpdate,
    ]
  );

  // Subscribe to event rooms when group chats are loaded
  useEffect(() => {
    const groupChats = chatState.groupChats || [];
    if (!groupChats.length) return;

    const eventIds = groupChats
      .map((groupChat) => groupChat.event_id || groupChat.eventId || groupChat.id)
      .filter(Boolean);

    if (eventIds.length > 0) {
      chatSocketService.subscribeEvents(eventIds);
    }
  }, [chatState.groupChats]);

  // Cleanup retry timeout on unmount
  useEffect(
    () => () => {
      if (groupChatsFetchRef.current.retryTimeout) {
        clearTimeout(groupChatsFetchRef.current.retryTimeout);
      }
    },
    []
  );

  return {
    loadGroupChats,
    loadGroupChatMessages,
    loadMoreGroupMessages,
    joinGroupChat,
    leaveGroupChat,
    sendGroupMessage,
    markGroupChatAsRead,
  };
}
